import { createSelector } from '@reduxjs/toolkit'
import {
  activeSheetSelector,
  entitiesSelector,
  entriesSelector,
} from 'apps/formsCreationKit/store/selectors/entities'
import { activeEntryGuidSelector } from 'apps/formsCreationKit/store/selectors/settings'
import { isTrueStringMatch } from 'apps/builder/lib/utils'
import { getTableCellPosition } from 'apps/studio/lib/utils/entities'
import { selectors, lib } from '@gocanvas/client'
const {
  clientEntriesSelector,
  activeSheetSelector: activeClientSheetSelector,
} = selectors
const { orderByPosition, GC_BLANK_DEFAULT, BLANK, choiceTypes, dateTypes } = lib

export const pdfSelector = createSelector(entitiesSelector, ({ pdf }) => pdf)

export const pdfFileSelector = createSelector(
  pdfSelector,
  ({ pdf_path }) => pdf_path
)

export const globalStylesSelector = createSelector(
  pdfSelector,
  ({ styles }) => styles
)

export const globalPositionSelector = createSelector(
  pdfSelector,
  ({ position }) => position
)

export const pagesSelector = createSelector(
  entitiesSelector,
  ({ pages }) => pages
)

export const layoutsSelector = createSelector(
  entitiesSelector,
  ({ layouts }) => layouts
)

export const tableLayoutsSelector = createSelector(layoutsSelector, layouts =>
  layouts.filter(l => l.layout_type === 'table')
)

// Get all the table layouts that belong to a PDF page
export const pageTableLayoutsSelector = pageId =>
  createSelector(tableLayoutsSelector, tableLayouts =>
    tableLayouts.filter(tableLayout => tableLayout.page_id === pageId)
  )

export const activePageSelector = createSelector(
  pagesSelector,
  activeSheetSelector,
  (pages, activeSheet = {}) => {
    return pages.find(page => page.section_ids.includes(activeSheet.section_id))
  }
)

export const styledPagesSelector = createSelector(
  globalStylesSelector,
  globalPositionSelector,
  pagesSelector,
  (globalStyles, globalPosition, pages) =>
    pages.map(page => ({
      ...page,
      styles: {
        ...globalStyles,
        ...(page.styles || {}),
      },
      position: {
        ...globalPosition,
        ...page.position,
      },
    }))
)

export const pageDefaultPositionSelector = pageNumber =>
  createSelector(
    globalPositionSelector,
    pagesSelector,
    (globalPosition, pages) => {
      const page =
        pages.find(page => isTrueStringMatch(page.page_number, pageNumber)) ||
        {}
      const { position = {} } = page
      return {
        ...globalPosition,
        ...position,
      }
    }
  )

export const activeStyledPageSelector = createSelector(
  styledPagesSelector,
  activePageSelector,
  (styledPages, activePage) =>
    styledPages.find(page => page.id === activePage.id)
)

export const elementsSelector = createSelector(
  entitiesSelector,
  ({ elements }) => elements
)

export const styledElementsSelector = createSelector(
  elementsSelector,
  pagesSelector,
  globalStylesSelector,
  tableLayoutsSelector,
  (elements, pages, globalStyles, tableLayouts) => {
    const tableLayoutsById = new Map(tableLayouts.map(l => [l.id, l]))
    return elements.map(element => {
      const { page_id } = element
      const { styles: elementStyles = {} } = element
      const page = pages.find(page => page.id === page_id) || {}
      const { styles: pageStyles = {} } = page

      // Static table cells don't store their own position comes from
      // parent table's columns/rows so consumers see a consistent shape.
      const tableLayout = tableLayoutsById.get(element.layout_id)
      const position = tableLayout
        ? getTableCellPosition(tableLayout, element.sort_order, {
            origin: 'page',
          })
        : element.position

      return {
        ...element,
        position,
        styles: {
          ...globalStyles,
          ...pageStyles,
          ...elementStyles,
        },
      }
    })
  }
)

export const visibleStyledElementsWorkspaceSelector = createSelector(
  styledElementsSelector,
  entriesSelector,
  (elements, entries) =>
    elements.filter(element => {
      const { entry_guid } = element
      const entry = entries.find(({ guid }) => guid === entry_guid) || {}
      return entry.pdf_visibility === 1 ? false : true
    })
)

// Create faux elements to populate the static table in preview, since client entries are not generated until the user actually adds rows
const processTableElements = (tableElements, tableLayouts) => {
  const tableElementGroups = Object.values(
    tableElements.reduce((groups, element) => {
      const { layout_id } = element
      if (!groups[layout_id]) {
        groups[layout_id] = []
      }
      groups[layout_id].push(element)
      return groups
    }, {})
  )

  const processedTableElements = []

  tableElementGroups.forEach(elementGroup => {
    const sortedElementGroup = elementGroup.sort(
      (a, b) => a.sort_order - b.sort_order
    )
    const tableLayout = tableLayouts.find(
      tableLayout => tableLayout.id === elementGroup[0].layout_id
    )
    const { rows = [] } = tableLayout
    const rowsToCreate = rows.length - 1
    let sortOrder =
      sortedElementGroup[sortedElementGroup.length - 1]?.sort_order

    processedTableElements.push(...sortedElementGroup)

    for (let i = 0; i < rowsToCreate; i++) {
      sortedElementGroup.forEach(element => {
        processedTableElements.push({
          ...element,
          sort_order: ++sortOrder,
        })
      })
    }
  })

  return processedTableElements
}

// Hide elements that are not pdf visible, apply style cascade, and apply table specific positioning if element is in a table
const checkVisAndApplyStyles = ({
  elements,
  entriesByGuid,
  tableLayoutsById,
  globalStyles = {},
  pageStyles = {},
}) => {
  return elements.reduce((acc, element) => {
    const tableLayout = tableLayoutsById.get(element.layout_id)
    const entry = entriesByGuid.get(element.entry_guid)
    // We only filter out normal screen elements here; we need the faux table elements
    // to be sent down regardless and we handle the visibility down stream. Filtering
    // them out here would cause the empty cells to not appear in the table.
    if (!tableLayout && entry && entry.pdf_visibility === 1) {
      return acc
    }

    const position = tableLayout
      ? getTableCellPosition(tableLayout, element.sort_order)
      : element.position

    acc.push({
      ...element,
      position,
      styles: {
        ...globalStyles,
        ...pageStyles,
        ...element.styles,
      },
    })

    return acc
  }, [])
}

// only the visible elements for a pdf preview in the test client
export const visibleStyledElementsPreviewSelector = createSelector(
  elementsSelector,
  pagesSelector,
  activeClientSheetSelector,
  entriesSelector,
  globalStylesSelector,
  tableLayoutsSelector,
  (elements, pages, activeClientSheet, entries, globalStyles, tableLayouts) => {
    const activePage = pages.find(page =>
      page.section_ids.includes(activeClientSheet?.section_id)
    )

    if (!activePage) {
      return { normalScreenElements: [], tableScreenElements: [] }
    }

    const pageStyles = activePage.styles || {}

    const tableLayoutsById = new Map(tableLayouts.map(l => [l.id, l]))

    const entriesByGuid = new Map(entries.map(entry => [entry.guid, entry]))

    const { tableElements, regularElements } = elements.reduce(
      (acc, element) => {
        if (!isTrueStringMatch(element.page_id, activePage.id)) return acc

        if (tableLayoutsById.get(element.layout_id)) {
          acc.tableElements.push(element)
        } else {
          acc.regularElements.push(element)
        }
        return acc
      },
      { tableElements: [], regularElements: [] }
    )

    const processedTableElements = processTableElements(
      tableElements,
      tableLayouts
    )

    const normalScreenElements = checkVisAndApplyStyles({
      elements: regularElements,
      entriesByGuid,
      tableLayoutsById,
      globalStyles,
      pageStyles,
    })

    const tableScreenElements = checkVisAndApplyStyles({
      elements: processedTableElements,
      entriesByGuid,
      tableLayoutsById,
      globalStyles,
      pageStyles,
    })

    return { normalScreenElements, tableScreenElements }
  }
)

export const activeStyledElementSelector = createSelector(
  styledElementsSelector,
  activeEntryGuidSelector,
  (elements, activeEntryGuid) => {
    return elements.find(element => element.entry_guid === activeEntryGuid)
  }
)

export const pageEntriesSelector = pageId =>
  createSelector(elementsSelector, entriesSelector, (elements, entries) => {
    const pageElements = elements.filter(element => element.page_id === pageId)
    const pageEntries = entries.filter(entry =>
      pageElements.some(element => element.entry_guid === entry.guid)
    )
    return pageEntries.sort(orderByPosition)
  })

export const pdfSchemaSelector = createSelector(
  pdfSelector,
  pagesSelector,
  elementsSelector,
  (pdf, pages, elements) => {
    return {
      ...pdf,
      pages,
      elements,
    }
  }
)

const normalizeElementValue = (value, type, label) => {
  if (Array.isArray(value) && type === 'Multiple Choice') {
    return value.filter(x => x !== GC_BLANK_DEFAULT).join('\n')
  }

  if (choiceTypes.includes(type) && value === GC_BLANK_DEFAULT) {
    return ''
  }

  if (dateTypes.includes(type) && value === BLANK) {
    return ''
  }

  if (Array.isArray(value) && type === 'Photo') {
    return value.map(({ url, type }) => ({ mimeType: type, url }))
  }

  if (value && type === 'Signature') {
    return {
      mimeType: 'image/jpeg',
      url: value,
    }
  }

  if (type === 'Static Text') {
    return value || label
  }

  return value || ''
}

export const testModePdfSchemaSelector = createSelector(
  pdfSchemaSelector,
  clientEntriesSelector,
  (pdfSchema, clientEntries) => {
    return {
      ...pdfSchema,
      watermark: 'Test Submission',
      elements: pdfSchema.elements.reduce((acc, element) => {
        const {
          value = '',
          type,
          pdf_visibility,
          label,
        } = clientEntries.find(entry => entry.guid === element.entry_guid) || {}

        if (pdf_visibility != 1) {
          acc.push({
            ...element,
            value: normalizeElementValue(value, type, label),
          })
        }
        return acc
      }, []),
    }
  }
)
